import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  ToggleButton,
  Tooltip,
  Typography,
} from '@mui/material'
import * as Icons from '@mui/icons-material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { ClosingAwareModal } from '~/components/closing-aware-modal'
import { TpToggleButtonGroup } from '~/components'
import { supabaseGetter } from '~/api/supabase-getter'
import { getDescendantOrgs } from '~/common/use-descendant-orgs'
import { useFarmChildren } from '~/crop/crop-modal/hooks/use-farm-children'
import { useFarmClimate } from '~/map/use-farm-climate'
import { doyToJSDate } from '~/common/utils/doy-utils'
import {
  RANGE_START_LFD_OFFSET_MONTHS,
  RANGE_END_FFD_OFFSET_MONTHS,
} from '~/crop/crop-schedule/crop-timeline/timeline-constants'
import { buildICS } from '../hooks/use-tasks-ics-export'

const cropLabel = (crop) => {
  const name = crop.name || crop.crop_templates?.name || 'Unknown'
  return `${name} ×${crop.count || 1}`
}

const CropCheckboxList = ({ crops, selectedIds, onChange }) => {
  const allSelected = crops.length > 0 && crops.every(c => selectedIds.has(c.id))
  const someSelected = crops.some(c => selectedIds.has(c.id))

  const handleToggleAll = () => {
    if (allSelected) {
      onChange(new Set())
    } else {
      onChange(new Set(crops.map(c => c.id)))
    }
  }

  const handleToggle = (crop) => () => {
    const next = new Set(selectedIds)
    if (next.has(crop.id)) {
      next.delete(crop.id)
    } else {
      next.add(crop.id)
    }
    onChange(next)
  }

  return (
    <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
      <List dense component="div" role="list" disablePadding>
        {crops.length === 0 ? (
          <ListItemButton disabled sx={{ py: 0, pl: 0.5 }}>
            <ListItemText
              primary={<Typography variant="caption" color="text.disabled">No crops found</Typography>}
            />
          </ListItemButton>
        ) : (
          <>
            <ListItemButton onClick={handleToggleAll} sx={{ py: 0, pl: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  tabIndex={-1}
                  disableRipple
                  size="small"
                  sx={{ maxHeight: '20px' }}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={700}>Select All</Typography>
                }
              />
            </ListItemButton>
            {crops.map((crop) => (
              <ListItemButton
                key={crop.id}
                role="listitem"
                onClick={handleToggle(crop)}
                sx={{ py: 0, pl: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Checkbox
                    checked={selectedIds.has(crop.id)}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                    sx={{ maxHeight: '20px' }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" noWrap>{cropLabel(crop)}</Typography>
                  }
                />
              </ListItemButton>
            ))}
          </>
        )}
      </List>
    </Paper>
  )
}

export const IcsExportModal = ({ open, onClose, farmId }) => {
  const [fromDate, setFromDate] = useState(dayjs())
  const [toDate, setToDate] = useState(dayjs().add(90, 'day'))
  const [eventMode, setEventMode] = useState('single')
  const [crops, setCrops] = useState([])
  const [selectedCropIds, setSelectedCropIds] = useState(new Set())
  const [loading, setLoading] = useState(false)

  const { climate } = useFarmClimate()
  const seasonStart = useMemo(() => {
    if (!climate?.avg_lfd) return null
    return dayjs(doyToJSDate(climate.avg_lfd)).subtract(RANGE_START_LFD_OFFSET_MONTHS, 'month')
  }, [climate])
  const seasonEnd = useMemo(() => {
    if (!climate?.avg_ffd) return null
    return dayjs(doyToJSDate(climate.avg_ffd)).add(RANGE_END_FFD_OFFSET_MONTHS, 'month')
  }, [climate])

  const { childOrgs } = useFarmChildren({ type: 'patch', farmId })
  const allOrgIds = useMemo(
    () => [farmId, ...childOrgs.map(o => o.id)].filter(Boolean),
    [farmId, childOrgs]
  )

  useEffect(() => {
    async function fetchCrops() {
      if (!open || allOrgIds.length === 0) return
      const { data, error } = await supabaseGetter.crops.getByOrganizations(allOrgIds)
      if (error) { console.error('[IcsExportModal] crops fetch error:', error); return }
      setCrops(data || [])
      setSelectedCropIds(new Set((data || []).map(c => c.id)))
    }
    fetchCrops()
  }, [open, allOrgIds])

  const handleExport = async () => {
    if (!farmId) return
    setLoading(true)
    try {
      const descendantIds = await getDescendantOrgs(farmId, 'farm')
      const allTaskOrgIds = [farmId, ...descendantIds]
      const { data, error } = await supabaseGetter.tasks.getByOrganizations(allTaskOrgIds)
      if (error) throw error

      const from = fromDate.startOf('day')
      const to = toDate.endOf('day')

      const filtered = (data || []).filter(task => {
        if (!task.task_window_start) return false
        const d = dayjs(task.task_window_start)
        if (d.isBefore(from) || d.isAfter(to)) return false
        return task.crop_id === null || selectedCropIds.has(task.crop_id)
      })

      const icsContent = buildICS(filtered, { mode: eventMode })
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'tasks.ics'
      a.click()
      URL.revokeObjectURL(url)
      onClose()
    } catch (err) {
      console.error('[IcsExportModal] export failed:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ClosingAwareModal
        open={open}
        onClose={onClose}
        loading={loading}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          overflowY: 'auto',
          py: 4,
        }}
      >
        <Box
          sx={{
            width: { xs: '90vw', md: '60vw', lg: '40vw' },
            bgcolor: 'background.paper',
            borderRadius: '0.5rem',
            boxShadow: 24,
            p: '1rem 2rem',
            my: 'auto',
          }}
        >
          <Typography variant="h5" marginBottom={1}>Export Tasks to ICS</Typography>
          <Divider sx={{ mb: 1 }} />

          <Stack spacing={2} pt={1}>
            {/* Date range */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Season start">
                <span>
                  <IconButton
                    size="small"
                    disabled={!seasonStart}
                    onClick={() => seasonStart && setFromDate(seasonStart)}
                  >
                    <Icons.NotStarted sx={{ transform: 'scaleX(-1)', color: 'primary.main' }} fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <DatePicker
                label="From"
                value={fromDate}
                onChange={(v) => v && setFromDate(v)}
                format="MMM DD YYYY"
                slotProps={{
                  textField: { size: 'small', fullWidth: true }
                }}
              />
              <DatePicker
                label="To"
                value={toDate}
                onChange={(v) => v && setToDate(v)}
                format="MMM DD YYYY"
                slotProps={{
                  textField: { size: 'small', fullWidth: true }
                }}
              />
              <Tooltip title="Season end">
                <span>
                  <IconButton
                    size="small"
                    disabled={!seasonEnd}
                    onClick={() => seasonEnd && setToDate(seasonEnd)}
                  >
                    <Icons.NotStarted sx={{ color: 'primary.main' }} fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            {/* Event window toggle */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2">Event window</Typography>
              <TpToggleButtonGroup
                value={eventMode}
                exclusive
                onChange={(_, v) => v && setEventMode(v)}
                size="small"
              >
                <ToggleButton value="single">Single Day</ToggleButton>
                <ToggleButton value="duration">Duration</ToggleButton>
              </TpToggleButtonGroup>
            </Stack>

            {/* Crop selector */}
            <Box>
              <Typography variant="body2" fontWeight={700} pb={0.5}>Crops</Typography>
              <CropCheckboxList
                crops={crops}
                selectedIds={selectedCropIds}
                onChange={setSelectedCropIds}
              />
              <Typography variant="caption" color="text.secondary">
                Farm/plot tasks (no crop) are always included.
              </Typography>
            </Box>
          </Stack>

          <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button variant="outlined" onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              disabled={loading}
              onClick={handleExport}
              endIcon={<Icons.CalendarMonth fontSize="small" />}
            >
              Export ICS
            </Button>
          </Box>
        </Box>
      </ClosingAwareModal>
    </LocalizationProvider>
  )
}
